/**
 * Mobile Roster - Position Management Page
 * Shows 4 tables: Active Lineup (slot-based), Bench, DL, Minors
 * Always in 3-line mode with Move/Drop action buttons
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { dynastyTheme, dynastyTileStyle } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { formatApiError } from '../../../services/errorUtils';
import { MobileDynastyTable } from '../components/MobileDynastyTable';
// (tile-style import removed — roster sections are flush, no card)
import { getStatConfigs, DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../../utils/statMapping';
import { createMobileBattersRosterColumns, createMobilePitchersRosterColumns } from '../components/mobileRosterColumns';
import MobilePositionAssignmentModal from '../components/MobilePositionAssignmentModal';
import MobileTableSectionHeader from '../components/MobileTableSectionHeader';
import MobileRosterSummaryBar from '../components/MobileRosterSummaryBar';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobileFASwapSheet from './optimizer/MobileFASwapSheet';
import { useCommissioner } from '../../../contexts/CommissionerContext';
import CommissionerToggle from '../../commissioner/CommissionerToggle';
import CommissionerModeBar from '../../commissioner/CommissionerModeBar';
import PromotionConfirmModal from '../../common/PromotionConfirmModal';
import { requiresPromotionConfirmation, computePromotionPreview } from '../../../utils/rosterMoveGuards';
import { classifyPlayerSection } from '../../../utils/playerClassification';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidatePlayer } from '../components/player/hooks/playerDataCache';

// Per-section accents sourced from the shared color service.
const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
const SECTION_AMBER = dynastyTheme.tokens.colors.warning;  // #f59e0b
const SECTION_RED = dynastyTheme.tokens.colors.error;      // #ef4444
const SECTION_MUTED = C.neutral[400];  // Minor Leagues accent (per locked spec)

const MobileRoster = ({ leagueId, selectedTeamId: parentSelectedTeamId }) => {
  const { 
    isCommissionerMode, 
    activeTeamId,
    isCommissioner,
    enterCommissionerMode
  } = useCommissioner();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [players, setPlayers] = useState([]);
  const [league, setLeague] = useState(null);
  const [availableCash, setAvailableCash] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');
  const [expandedSections, setExpandedSections] = useState({
    bench: true,
    dl: false,
    minors: false
  });
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  // 9f.3 — promotion-confirmation modal for minors-exit of price-less players
  const [pendingPromotion, setPendingPromotion] = useState(null);
  
  // Player modal/card state
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isPlayerCardOpen, setIsPlayerCardOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  // DD-RO-MOBILE-SWAP-0727: player-initiated 1-1 swap (ROSTER direction — the mirror of the FA side). This
  // roster player is the DROP; getOptimizerAlternatives supplies the FA candidates for HIS slot (the same
  // per-player alternatives the desktop field-view swap shows). Pick one → MobileFASwapSheet {player: FA,
  // drop: this player} → canonical generatePlanForDrop + executePlan. No new mutation path.
  const [swapDropPlayer, setSwapDropPlayer] = useState(null);   // the roster player being replaced
  const [swapAlts, setSwapAlts] = useState(null);               // FA candidates for his slot ([] = none)
  const [swapRec, setSwapRec] = useState(null);                 // {player, drop} handed to the sheet
  const handleSwapRoster = useCallback(async (player) => {
    setSwapDropPlayer(player);
    setSwapAlts(null);
    try {
      const res = await leaguesAPI.getOptimizerAlternatives(leagueId, player?.ids?.mlb, parentSelectedTeamId);
      const list = res?.alternatives || res?.candidates || [];
      setSwapAlts(list.map((a) => a.player || a).filter((p) => p?.ids?.mlb));
    } catch (e) {
      setSwapAlts([]);
    }
  }, [leagueId, parentSelectedTeamId]);
  const pickSwapAlternative = useCallback((fa) => {
    if (swapDropPlayer) {
      setSwapRec({ player: fa, drop: { player_id: swapDropPlayer.ids?.mlb, player_name: swapDropPlayer.info?.full_name } });
    }
    setSwapDropPlayer(null);
    setSwapAlts(null);
  }, [swapDropPlayer]);
  
  // Team selection state
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(parentSelectedTeamId || null);
  const [userTeamId, setUserTeamId] = useState(null);

  // Sync with parent's team selection
  useEffect(() => {
    if (parentSelectedTeamId) {
      setSelectedTeamId(parentSelectedTeamId);
    }
  }, [parentSelectedTeamId]);

  useEffect(() => {
    if (leagueId) {
      loadAllTeams();
      loadData();
    }
  }, [leagueId]);

  useEffect(() => {
    if (leagueId) {
      loadData();
    }
  }, [selectedTeamId, isCommissionerMode, activeTeamId]);

  const loadAllTeams = async () => {
    try {
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          manager_name: team.manager_name || 'Unknown Manager',
          is_user_team: team.is_user_team || false
        }));
        
        setAllTeams(teamList);
        
        const userTeam = teamList.find(t => t.is_user_team);
        if (userTeam) {
          setUserTeamId(userTeam.team_id);
          if (!selectedTeamId && !isCommissionerMode) {
            setSelectedTeamId(userTeam.team_id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Determine which team to load
      let teamId = null;
      if (isCommissionerMode && activeTeamId) {
        teamId = activeTeamId;
      } else if (selectedTeamId) {
        teamId = selectedTeamId;
      } else if (userTeamId) {
        teamId = userTeamId;
      }
      
      // Fetch both league details and roster
      const [leagueResponse, rosterResponse] = await Promise.all([
        leaguesAPI.getLeagueDetails(leagueId),
        teamId && teamId !== userTeamId
          ? leaguesAPI.getTeamRosterCanonical(leagueId, teamId)
          : leaguesAPI.getMyRosterCanonical(leagueId)
      ]);
      
      if (leagueResponse && leagueResponse.success) {
        setLeague(leagueResponse.league);
      }
      
      if (rosterResponse && rosterResponse.success) {
        console.log('Loaded roster:', rosterResponse.players?.length, 'players');
        setPlayers(rosterResponse.players || []);
        // FAAB source (§4.5.6.1): the backend ledger balance, same field desktop
        // reads. null when the ledger didn't respond → the bar shows an explicit
        // unavailable state, never a fabricated number.
        setAvailableCash(rosterResponse.available_cash ?? null);
      }
    } catch (err) {
      console.error('Error loading roster:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
  };

  // Get league positions from position_requirements (MATCHES DESKTOP)
  const getLeaguePositions = () => {
    const positions = {
      hitting: [],
      pitching: []
    };

    if (league?.roster_settings?.position_requirements && typeof league.roster_settings.position_requirements === 'object') {
      const posReqs = league.roster_settings.position_requirements;
      
      Object.entries(posReqs).forEach(([positionKey, config]) => {
        const slots = config?.slots || 0;
        
        if (positionKey === 'P' && slots > 0) {
          positions.pitching.push(...Array(slots).fill('P'));
        } else if (slots > 0 && !['BN', 'DL', 'MIN'].includes(positionKey)) {
          // Don't create slots for bench/DL/minors
          positions.hitting.push(...Array(slots).fill(positionKey));
        }
      });
    } else {
      // Fallback defaults
      positions.hitting = ['C', '1B', '2B', 'SS', '3B', 'OF', 'OF', 'OF', 'UTIL'];
      positions.pitching = Array(10).fill('P');
    }
    
    return positions;
  };

  const positions = getLeaguePositions();

  // ========================================
  // DYNAMIC STAT CATEGORIES FROM LEAGUE
  // ========================================
  const statCategories = useMemo(() => {
    if (league?.league_settings?.scoring_categories) {
      return {
        hitting: league.league_settings.scoring_categories.hitting || DEFAULT_BATTING_STATS,
        pitching: league.league_settings.scoring_categories.pitching || DEFAULT_PITCHING_STATS
      };
    }
    
    return {
      hitting: DEFAULT_BATTING_STATS,
      pitching: DEFAULT_PITCHING_STATS
    };
  }, [league]);

  // Get stat configs for current tab
  const currentStatConfigs = useMemo(() => {
    const statLabels = activeTab === 'hitting' ? statCategories.hitting : statCategories.pitching;
    const isPitcher = activeTab === 'pitching';
    return getStatConfigs(statLabels, isPitcher);
  }, [activeTab, statCategories]);

  // Event handlers - MUST be defined before useMemo calls
  const handleMoveClick = (player) => {
    // Player comes from column renderer - it's the canonical player object
    setSelectedPlayer(player);
    setShowPositionModal(true);
  };

  const handleDropClick = async (player) => {
    // Player comes from column renderer - it's the canonical player object
    const playerName = player?.info?.full_name || 'this player';
    // DD-WAIVER-DROPGUARD-0725 Unit A (§14.0 parity with desktop): warn if this player is the designated
    // drop on a pending waiver claim — cutting him voids that claim. Read-only pre-check; non-blocking.
    let confirmMsg = `Drop ${playerName}? This cannot be undone.`;
    const lpId = player?.ids?.league_player;
    if (lpId) {
      try {
        const impact = await leaguesAPI.getDropImpact(leagueId, lpId);
        if (impact?.waiver_claim_warning) {
          confirmMsg = impact.waiver_claim_warning;
        }
      } catch (e) {
        // best-effort — never block a legitimate drop on a lookup failure
      }
    }
    if (window.confirm(confirmMsg)) {
      dropPlayer(player);
    }
  };

  const handleTradeClick = (player) => {
    // TODO: Navigate to trade center with this player pre-selected
    const playerName = player?.info?.full_name || 'this player';
    console.log('Trade clicked for player:', playerName);
    alert('Trade feature coming soon! This will open the trade center.');
  };

  // Player click handler - opens modal (receives MLB ID from column renderer)
  const handlePlayerClick = (mlbId) => {
    console.log('Opening player modal for MLB ID:', mlbId);
    setSelectedPlayerId(mlbId?.toString());
    setIsPlayerModalOpen(true);
  };

  // Filter players by status - pass raw canonical data (NO transformation)
  const filteredPlayers = useMemo(() => {
    // Canonical section classification (Bible §4.5.9.1): active-slot players
    // classify by the slot they occupy (TWP at P_6 → pitching); reserve
    // players by primary position (TWP → hitting).
    const inSection = (section, status) => players.filter(p =>
      classifyPlayerSection(p) === section &&
      p.league_context?.roster?.status === status
    );
    return {
      hitting: {
        active: inSection('hitting', 'active'),
        bench:  inSection('hitting', 'bench'),
        dl:     inSection('hitting', 'dl'),
        minors: inSection('hitting', 'minors'),
      },
      pitching: {
        active: inSection('pitching', 'active'),
        bench:  inSection('pitching', 'bench'),
        dl:     inSection('pitching', 'dl'),
        minors: inSection('pitching', 'minors'),
      }
    };
  }, [players]);

  // Get raw canonical data for current tab (MobileDynastyTable handles everything)
  const category = activeTab;
  const activePlayers = filteredPlayers[category].active;
  const benchPlayers = filteredPlayers[category].bench;
  const dlPlayers = filteredPlayers[category].dl;
  const minorsPlayers = filteredPlayers[category].minors;

  // Create columns using mobile column creators (always 3-line mode for roster).
  // Bible § 14 stat-column rule: pass currentStatConfigs so dynamic stat
  // columns derive from league_settings.scoring_categories.
  const activeLineupColumns = useMemo(() => {
    if (activeTab === 'hitting') {
      return createMobileBattersRosterColumns({
        onMoveClick: handleMoveClick,
        onDropClick: handleDropClick,
        onTradeClick: handleTradeClick,
        onPlayerClick: handlePlayerClick,
        statConfigs: currentStatConfigs,
      });
    } else {
      return createMobilePitchersRosterColumns({
        onMoveClick: handleMoveClick,
        onDropClick: handleDropClick,
        onTradeClick: handleTradeClick,
        onPlayerClick: handlePlayerClick,
        statConfigs: currentStatConfigs,
      });
    }
  }, [activeTab, handleMoveClick, handleDropClick, handleTradeClick, handlePlayerClick, currentStatConfigs]);

  const reserveColumns = useMemo(() => {
    if (activeTab === 'hitting') {
      return createMobileBattersRosterColumns({
        onMoveClick: handleMoveClick,
        onDropClick: handleDropClick,
        onTradeClick: handleTradeClick,
        onPlayerClick: handlePlayerClick,
        statConfigs: currentStatConfigs,
      });
    } else {
      return createMobilePitchersRosterColumns({
        onMoveClick: handleMoveClick,
        onDropClick: handleDropClick,
        onTradeClick: handleTradeClick,
        onPlayerClick: handlePlayerClick,
        statConfigs: currentStatConfigs,
      });
    }
  }, [activeTab, handleMoveClick, handleDropClick, handleTradeClick, handlePlayerClick, currentStatConfigs]);

  // Modal expand to card
  const handleExpandToCard = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(true);
  };

  // Card collapse to modal
  const handleCollapseToModal = () => {
    setIsPlayerCardOpen(false);
    setIsPlayerModalOpen(true);
  };

  // Close everything
  const handleClosePlayer = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(false);
    setSelectedPlayerId(null);
  };

  // 9f.3 — single dispatch helper called either directly (no confirmation needed)
  // or via the promotion confirmation modal.
  const dispatchMove = async (moveData, onSuccess) => {
    try {
      const response = await leaguesAPI.moveRosterPlayer(leagueId, moveData);
      if (response.success) {
        await loadData();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      // DD-DL-DEADLOCK-0726-FE (§14.0 parity with MyRoster): err.message on an axios failure is
      // "Request failed with status code 422" — it hid the server's actual DL-lock explanation.
      console.error('Failed to update position:', err);
      alert(formatApiError(err, 'Error updating position'));
    }
  };

  const handleAssignPlayer = async ({ player, roster_status, roster_position }) => {
    const leaguePlayerId = player?.ids?.league_player;
    if (!leaguePlayerId) {
      alert('Error: Player ID not found');
      return;
    }

    const fromStatus = player.league_context?.roster?.status;
    const moveData = {
      league_player_id: leaguePlayerId,
      new_status: roster_status,
      target_position: roster_position,
      start_contract: roster_status === 'active' && fromStatus === 'minors',
    };

    const closePositionModal = () => {
      setShowPositionModal(false);
      setSelectedPlayer(null);
    };

    // 9f.3 — gate minors-exit promotions of price-less players behind confirmation modal.
    if (requiresPromotionConfirmation({ player, fromStatus, toStatus: roster_status })) {
      const playerName = `${player.info?.first_name ?? ''} ${player.info?.last_name ?? ''}`.trim() || 'Player';
      setPendingPromotion({ player, playerName, moveData, onAfterDispatch: closePositionModal });
      return;
    }

    await dispatchMove(moveData, closePositionModal);
  };

  const handlePromotionConfirm = async () => {
    if (!pendingPromotion) return;
    const { moveData, onAfterDispatch } = pendingPromotion;
    setPendingPromotion(null);
    await dispatchMove(moveData, onAfterDispatch);
  };

  const handlePromotionCancel = () => {
    setPendingPromotion(null);
  };

  const dropPlayer = async (player) => {
    try {
      // Player is canonical format - extract league_player_id
      const leaguePlayerId = player?.ids?.league_player;
      if (!leaguePlayerId) {
        alert('Error: Player ID not found');
        return;
      }

      const response = await leaguesAPI.dropPlayerFromTeam(leagueId, leaguePlayerId);
      invalidatePlayer(leagueId, player?.ids?.mlb);
      
      if (response.success) {
        await loadData(); // Reload roster
      }
    } catch (err) {
      console.error('Failed to drop player:', err);
      alert(formatApiError(err, 'Error dropping player'));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Columns are created above using desktop column creators

  if (loading) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: dynastyTheme.tokens.colors.neutral[400]
      }}>
        <div style={{
          width: '30px',
          height: '30px',
          border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 10px'
        }}></div>
        Loading roster...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: dynastyTheme.tokens.colors.error + '20',
        borderTop: `1px solid ${dynastyTheme.tokens.colors.error}`,
        borderBottom: `1px solid ${dynastyTheme.tokens.colors.error}`,
        color: dynastyTheme.tokens.colors.error
      }}>
        Error: {error}
      </div>
    );
  }

  const getCurrentTeamName = () => {
    if (isCommissionerMode && activeTeamId) {
      const team = allTeams.find(t => t.team_id === activeTeamId);
      return team?.team_name || 'Unknown Team';
    }
    if (selectedTeamId) {
      const team = allTeams.find(t => t.team_id === selectedTeamId);
      return team?.team_name || 'Unknown Team';
    }
    const userTeam = allTeams.find(t => t.is_user_team);
    return userTeam?.team_name || 'My Team';
  };

  return (
    <div>
      {/* Commissioner Mode Bar - Compact */}
      {isCommissionerMode && (
        <div style={{ marginBottom: '10px' }}>
          <CommissionerModeBar 
            league={league}
            onTeamSwitch={() => loadData()}
          />
        </div>
      )}

      {/* Team selector is in parent MobileTeamHome's MobilePageHeader */}

      {/* Roster summary bar — mobile-native (DD parity), same backend fields as
          desktop (payroll / FAAB / roster counts / expiring·OP / decision). */}
      <MobileRosterSummaryBar players={players} availableCash={availableCash} league={league} />

      {/* ACTIVE LINEUP — flush 3px-bar header; Hitters/Pitchers toggle INLINE
          right (replaces the old centered band), no curved tile. */}
      <div>
        <MobileTableSectionHeader
          label="Active Lineup"
          color={GOLD}
          tabs={{
            active: activeTab,
            onSelect: setActiveTab,
            options: ['hitting', 'pitching'].map((t) => ({
              key: t,
              label: `${t === 'hitting' ? 'Hitters' : 'Pitchers'} ${filteredPlayers[t].active.length + filteredPlayers[t].bench.length + filteredPlayers[t].dl.length + filteredPlayers[t].minors.length}`,
            })),
          }}
        />
        <MobileDynastyTable
          tableType="roster-lineup"
          leaguePositions={positions}
          playerType={activeTab}
          rowsPerItem={3}
          rowTypes={['season', 'rolling', 'accrued']}
          data={activePlayers}
          columns={activeLineupColumns}
          stickyHeader={false}
          enableHorizontalScroll={true}
          enableVerticalScroll={false}
          maxHeight="none"
          minWidth="560px"
        />
      </div>

      {/* BENCH — collapsible, flush */}
      {benchPlayers.length > 0 && (
        <div>
          <MobileTableSectionHeader label="Bench" color={SECTION_AMBER} count={benchPlayers.length} collapsed={!expandedSections.bench} onToggle={() => toggleSection('bench')} />
          {expandedSections.bench && (
            <MobileDynastyTable tableType="player-list" rowsPerItem={3} rowTypes={['season', 'rolling', 'accrued']} stickyHeader={false} enableHorizontalScroll={true} enableVerticalScroll={false} maxHeight="none" minWidth="560px" data={benchPlayers} columns={reserveColumns} />
          )}
        </div>
      )}

      {/* DISABLED LIST — collapsible, flush */}
      {dlPlayers.length > 0 && (
        <div>
          <MobileTableSectionHeader label="Disabled List" color={SECTION_RED} count={dlPlayers.length} collapsed={!expandedSections.dl} onToggle={() => toggleSection('dl')} />
          {expandedSections.dl && (
            <MobileDynastyTable tableType="player-list" rowsPerItem={3} rowTypes={['season', 'rolling', 'accrued']} stickyHeader={false} enableHorizontalScroll={true} enableVerticalScroll={false} maxHeight="none" minWidth="560px" data={dlPlayers} columns={reserveColumns} />
          )}
        </div>
      )}

      {/* MINOR LEAGUES — collapsible, flush */}
      {minorsPlayers.length > 0 && (
        <div>
          <MobileTableSectionHeader label="Minor Leagues" color={SECTION_MUTED} count={minorsPlayers.length} collapsed={!expandedSections.minors} onToggle={() => toggleSection('minors')} />
          {expandedSections.minors && (
            <MobileDynastyTable tableType="player-list" rowsPerItem={3} rowTypes={['season', 'rolling', 'accrued']} stickyHeader={false} enableHorizontalScroll={true} enableVerticalScroll={false} maxHeight="none" minWidth="560px" data={minorsPlayers} columns={reserveColumns} />
          )}
        </div>
      )}

      {/* POSITION ASSIGNMENT MODAL */}
      {showPositionModal && selectedPlayer && (
        <MobilePositionAssignmentModal
          player={selectedPlayer}
          league={league}
          currentRoster={players}
          onAssign={handleAssignPlayer}
          onCancel={() => {
            setShowPositionModal(false);
            setSelectedPlayer(null);
          }}
          isVisible={showPositionModal}
          mode="move"
          currentStatus={selectedPlayer.league_context?.roster?.status}
          currentPosition={selectedPlayer.league_context?.roster?.position}
        />
      )}

      {/* PLAYER MODAL */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={handleClosePlayer}
        onExpandToCard={handleExpandToCard}
        onSwap={handleSwapRoster}
      />

      {/* DD-RO-MOBILE-SWAP-0727: ROSTER-direction FA-candidate picker (the alternatives for the drop's slot),
          then MobileFASwapSheet confirms the 1-1 swap. Both reuse existing endpoints/components. */}
      {swapDropPlayer && (
        <div data-testid="swap-alt-picker"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={() => { setSwapDropPlayer(null); setSwapAlts(null); }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#111', borderTopLeftRadius: 12, borderTopRightRadius: 12, padding: 16, maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ color: '#fff', fontWeight: 700, marginBottom: 10 }}>
              Swap in for {swapDropPlayer.info?.full_name || 'player'}
            </div>
            {swapAlts == null && <div style={{ color: '#888' }}>Loading candidates…</div>}
            {swapAlts != null && swapAlts.length === 0 && <div style={{ color: '#888' }}>No eligible free-agent candidates.</div>}
            {(swapAlts || []).map((fa) => (
              <button key={fa.ids?.mlb} type="button" data-testid="swap-alt-row"
                onClick={() => pickSwapAlternative(fa)}
                style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
                  background: 'transparent', border: 'none', borderTop: '1px solid #222', padding: '10px 0', color: '#eee', cursor: 'pointer' }}>
                <span>{fa.info?.full_name || 'Free agent'}</span>
                <span style={{ color: '#4ade80', fontWeight: 700 }}>Swap</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {swapRec && (
        <MobileFASwapSheet rec={swapRec} leagueId={leagueId}
          onClose={() => setSwapRec(null)} onComplete={() => setSwapRec(null)} />
      )}

      {/* PLAYER CARD */}
      <MobilePlayerCard
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerCardOpen}
        onClose={handleClosePlayer}
        onCollapse={handleCollapseToModal}
      />

      {/* 9f.3 — Promotion confirmation modal (minors-exit of price-less players) */}
      {pendingPromotion && (() => {
        const { cost, contractYears } = computePromotionPreview({ league });
        return (
          <PromotionConfirmModal
            isOpen
            player={pendingPromotion.player}
            playerName={pendingPromotion.playerName}
            destination={pendingPromotion.moveData.new_status}
            cost={cost}
            contractYears={contractYears}
            onConfirm={handlePromotionConfirm}
            onCancel={handlePromotionCancel}
          />
        );
      })()}
    </div>
  );
};

export default MobileRoster;
